import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import { IconWrapper } from "@insite/mobius/Icon";
import ShoppingBag from "@insite/mobius/Icons/ShoppingBag";
import Truck from "@insite/mobius/Icons/Truck";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import toggleLoginDrawer from "@NA/Handlers/ADILoginDrawerHandler";
import toggleRewardsDrawer from "@NA/Handlers/ADIRewardsDrawerHandler";
import Cart from "@NA/Icons/cart";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import ADIColors from "@NA/Utils/ADIColors";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
}

const mapStateToProps = (state: CustomApplicationState) => {
    const location = getLocation(state).pathname;
    const isCheckoutPage = location === "/Checkout/ReviewAndPay";
    const pageType = getCurrentPage(state).type;
    const cartValue = getCurrentCartState(state).value?.cartLines;
    const cart = getCurrentCartState(state).value;
    const session = getSession(state);
    const callCartApi = session.isAuthenticated;

    return {
        shouldLoadCart: !getCurrentCartState(state).value && !getCurrentCartState(state).isLoading,
        totalCountDisplay: getCurrentCartState(state).value?.totalCountDisplay,
        cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
        session,
        isCartLoading: getCurrentCartState(state).isLoading || state.ADIGlobalData.ADIUpdateCartLoading.loading,
        isCheckoutPage,
        pageType,
        cartValue,
        callCartApi,
        cart,
    };
};

const mapDispatchToProps = {
    loadCurrentCart,
    toggleLoginDrawer,
    toggleRewardsDrawer,
};

export interface CartLinkStyles {
    routerLink?: LinkPresentationProps;
    desktopCountText?: TypographyPresentationProps;
    mobileCountText?: TypographyPresentationProps;
    compactCountText?: TypographyPresentationProps;
    cartText?: TypographyPresentationProps;
}

export const cartLinkStyles: CartLinkStyles = {
    routerLink: {
        typographyProps: {
            variant: "headerSecondary",
        },
        icon: {
            iconProps: {
                size: 25,
            },
        },
        color: `${ADIColors.darkBlue1}`, // hardcoded colors as style guide does not have this color
        css: css`
            padding: 0px;
            position: relative;
            display: block !important;
            margin-bottom: 0px;
            &:focus {
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            null,
                            css`
                                outline-color: ${ADIColors.darkBlack3};
                                outline-style: ${getProp("theme.focus.style", "solid")};
                                outline-width: 0px;
                            `,
                        ],
                        "min",
                    );
                }}
            }
            &:focus-visible {
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            null,
                            css`
                                outline-color: ${ADIColors.darkBlack3};
                                outline-style: ${getProp("theme.focus.style", "solid")};
                                outline-width: 1px;
                                outline-offset: 1px;
                            `,
                        ],
                        "min",
                    );
                }}
            }

            ${IconWrapper} {
                margin-bottom: 2px;
                margin-right:  4px; 
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                color: ${getColor("common.background")} !important;                                                               
                            `,
                            null,
                            null,
                            null,
                        ],
                        "max",
                    );
                }}
            }

            &:hover {
                .header-cart-text {
                    text-decoration: underline;
                }
            }
            ${TypographyStyle} {
                color: #002856;
                font-size: 9.8px;
                font-weight: 400;
                line-height: normal;
                text-transform: capitalize;
                white-space: nowrap;
                display: block;               
                padding-top: 2px;
                padding-left: -4px;
                margin-top: -2px;
               
                > ${TypographyStyle} {
                    &:nth-child(2) {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 9.8px;
                        line-height: 10px;
                        font-weight: 700;                   
                    }
                }
            }
        `,
    },

    compactCountText: {
        css: css`
            font-weight: normal;
        `,
    },
    mobileCountText: {
        css: css`
            background: ${getColor("common.background")};
            border: 1px solid ${ADIColors.darkBlue1};
            width: 22px;
            height: 22px;
            display: flex;
            justify-content: center;
            align-items: center;
            border-radius: 11px;
            color: ${getColor("secondary")};
            text-align: center;
            font-size: 14px;
            position: absolute;
            left: 9px;
            top: -7px;
            font-weight: 500;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            right: 2px;
                            top: -3px;                           
                        `,
                        css`
                            right: 2px;
                        `,
                        css`
                            right: 2px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    cartText: {
        css: css`
            color: ${getColor("secondary")};
            font-size: 10px;
            font-family: Nunito Sans;
            font-weight: 400;
            line-height: normal;
            display: block;
            width: auto;
            white-space: nowrap;
        `,
    },
};

const iconMap = {
    cart: Cart,
    truck: Truck,
    bag: ShoppingBag,
    none: undefined,
} as const;

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: "both" | "label" | "icon";
        [fields.icon]: keyof typeof iconMap;
    };
    isCompactHeaderCart?: boolean;
    theme: BaseTheme;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

/** Reloading the cart makes `totalCountDisplay` temporarily unavailable, so retain the previous value for better UX. */
let retainedTotalCountToDisplay: number | undefined;
const styles = cartLinkStyles;
const CartLink: FC<Props> = ({
    shouldLoadCart,
    cartUrl,
    totalCountDisplay,
    fields: { visibilityState, icon: selectedIcon },
    loadCurrentCart,
    session,
    toggleLoginDrawer,
    toggleRewardsDrawer,
    isCheckoutPage,
    isCartLoading,
    pageType,
    cartValue,
    callCartApi,
    cart,
}) => {
    const history = useHistory();
    const [loadCart, setLoadCart] = useState(true);

    useEffect(() => {
        if (callCartApi) {
            const isShouldLoadFullCart = pageType === "CheckoutReviewAndSubmitPage";
            if (pageType) {
                loadCurrentCart({ shouldLoadFullCart: isShouldLoadFullCart, replaceCart: true });
            }
        }
    }, []);

    const showIcon = visibilityState !== "both";
    const showLabel = visibilityState !== "both";

    if (typeof totalCountDisplay !== "undefined") {
        if (
            (pageType !== "CheckoutReviewAndSubmitPage" && cart?.type !== "Quote") ||
            (pageType !== "OrderConfirmationPage" && cart?.type !== "Quote")
        ) {
            retainedTotalCountToDisplay = totalCountDisplay;
        }
    }

    const icon = iconMap[(showIcon && selectedIcon) || "none"];

    const { routerLink } = styles;
    const isAuthenticated = session && (session.isAuthenticated || session.rememberMe) && !session.isGuest;

    const redirectToURL = () => {
        if (isAuthenticated) {
            history.push(`${cartUrl}`);
            toggleLoginDrawer({ loginDrawer: false });
            toggleRewardsDrawer({ rewardsDrawer: false });
        } else {
            toggleLoginDrawer({ loginDrawer: true });
            toggleRewardsDrawer({ rewardsDrawer: true });
        }
    };
    return (
        <>
            {/* {isCartLoading && !isCheckoutPage ? (
                <img
                    height={24}
                    width={24}
                    src="/UserFiles/US/userfiles/Themes/Images/output-onlinetools.gif?width=128&height=128"
                />
            ) : (
                <Link
                    {...routerLink}
                    onClick={() => {
                        if (!isCheckoutPage) {
                            redirectToURL();
                        } else {
                            if (!isCartLoading) {
                                redirectToURL();
                            }
                        }
                    }}
                    icon={{ ...routerLink?.icon, iconProps: { src: icon, ...routerLink?.icon?.iconProps } }}
                    data-test-selector="cartLink"
                >
                    <Hidden below="lg" as="span">
                        {showLabel && isAuthenticated && `${translate("Cart")} `}
                    </Hidden>

                    {isAuthenticated && (
                        <Typography as="span" {...styles.mobileCountText}>
                            {retainedTotalCountToDisplay} {isCartLoading}
                        </Typography>
                    )}
                    <VisuallyHidden>{siteMessage("Header_CartCount")}</VisuallyHidden>
                </Link>
            )} */}

            <Link
                {...routerLink}
                onClick={() => {
                    if (!isCheckoutPage) {
                        redirectToURL();
                    } else if (!isCartLoading) {
                            redirectToURL();
                    }
                }}
                icon={{ ...routerLink?.icon, iconProps: { src: icon, ...routerLink?.icon?.iconProps } }}
                data-test-selector="cartLink"
            >
                <Hidden below="lg" as="span">
                    {showLabel && isAuthenticated && `${translate("Cart")} `}
                </Hidden>

                {isAuthenticated && (
                    <Typography as="span" {...styles.mobileCountText}>
                        {retainedTotalCountToDisplay || '0'}
                    </Typography>
                )}
                <VisuallyHidden>{siteMessage("Header_CartCount")}</VisuallyHidden>
            </Link>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CartLink),
    definition: {
        displayName: "Cart",
        icon: "cart-shopping",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Show Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label Only",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon Only",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "Shopping Cart Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "None",
                        value: "none",
                    },
                    {
                        displayName: "Bag",
                        value: "bag",
                    },
                    {
                        displayName: "Cart",
                        value: "cart",
                    },
                    {
                        displayName: "Truck",
                        value: "truck",
                    },
                ],
                defaultValue: "cart",
                fieldType: "General",
                sortOrder: 2,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
